import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

type AppRole = 'super_admin' | 'admin' | 'tecnico' | 'gestor';

interface PrivateRouteProps {
  children: React.ReactNode;
  requireRole?: AppRole;
  requireAnyRole?: AppRole[];
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ children, requireRole, requireAnyRole }) => {
  const { user, perfil, empresa, hasRole, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Portal users must never enter the app
  if (perfil?.rol === 'cliente_portal') {
    return <Navigate to="/portal" replace />;
  }

  // Check if empresa is suspended (unless super_admin)
  if (empresa && !hasRole('super_admin')) {
    if (empresa.estado === 'suspendida') {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold mb-4">Cuenta Suspendida</h1>
            <p className="text-muted-foreground">
              La suscripción de tu empresa ha sido suspendida. 
              Por favor contacta con el administrador.
            </p>
          </div>
        </div>
      );
    }
    
    if (empresa.estado === 'cancelada') {
      return (
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="text-center max-w-md">
            <h1 className="text-2xl font-bold mb-4">Cuenta Cancelada</h1>
            <p className="text-muted-foreground">
              La cuenta de tu empresa ha sido cancelada.
            </p>
          </div>
        </div>
      );
    }
  }

  // Check single role requirement
  if (requireRole && !hasRole(requireRole)) {
    return <Navigate to="/" replace />;
  }

  // Check any role requirement (OR logic)
  if (requireAnyRole && requireAnyRole.length > 0) {
    const hasAnyRole = requireAnyRole.some(role => hasRole(role));
    if (!hasAnyRole) {
      return <Navigate to="/" replace />;
    }
  }

  return <>{children}</>;
};

export default PrivateRoute;
